import type { Editor } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { plainOffsetToPos, resolveCommentRange } from "./commentOffsets";

export type CommentDecorationRange = {
  id: string;
  from?: number;
  to?: number;
  quote?: string;
};

export type CommentDecorationOptions = {
  ranges: CommentDecorationRange[];
  visible: boolean;
};

const key = new PluginKey<CommentDecorationOptions>("smCommentDecorations");

export const CommentDecorations = Extension.create<CommentDecorationOptions>({
  name: "smCommentDecorations",

  addOptions() {
    return {
      ranges: [],
      visible: true,
    };
  },

  addProseMirrorPlugins() {
    const extension = this;
    return [
      new Plugin({
        key,
        state: {
          init: () => ({
            ranges: extension.options.ranges,
            visible: extension.options.visible,
          }),
          apply(tr, value) {
            const meta = tr.getMeta(key) as
              | Partial<CommentDecorationOptions>
              | undefined;
            if (!meta) return value;
            return { ...value, ...meta };
          },
        },
        props: {
          decorations(state) {
            const opts = key.getState(state) ?? extension.options;
            if (!opts.visible || opts.ranges.length === 0) {
              return DecorationSet.empty;
            }
            const content = state.doc.textBetween(
              0,
              state.doc.content.size,
              "\n",
              "",
            );
            const decorations = [];
            for (const range of opts.ranges) {
              const resolved = resolveCommentRange(
                content,
                range.from,
                range.to,
                range.quote,
              );
              if (!resolved) continue;
              const pmFrom = plainOffsetToPos(state.doc, resolved.from);
              const pmTo = plainOffsetToPos(state.doc, resolved.to);
              if (pmFrom === null || pmTo === null || pmTo <= pmFrom) continue;
              decorations.push(
                Decoration.inline(pmFrom, pmTo, {
                  class: "sm-comment-mark",
                  "data-comment-id": range.id,
                }),
              );
            }
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

export function setCommentDecorationOptions(
  editor: Editor,
  options: Partial<CommentDecorationOptions>,
) {
  if (editor.isDestroyed) return;
  try {
    editor.view.dispatch(editor.state.tr.setMeta(key, options));
  } catch {
    /* Editor view already torn down during scene remount. */
  }
}
